import dayjs, { Dayjs } from 'dayjs';
import {
  BaseExerciseTemplate,
  DistanceUnit,
  DistanceUnitShort,
  exactKgtoLbs,
  exactMetersToMiles,
  ExerciseInstructionsStep,
  ExerciseType,
  exhaustiveTypeCheck,
  FormatAtText,
  Result,
  SetType,
  StrengthLevel,
  UserExerciseSet,
  UserFacingSetIndicator,
  Weekday,
  WeightUnit,
} from '.';

/**
 * Doesn't matter what you throw in the function it'll
 * always return a number. Non number values will return
 * 0.
 */
export const num = (value?: any): number => {
  if (typeof value !== 'number') return 0;
  return value ?? 0;
};

export const clampNumber = (value: number, limits: { min?: number; max?: number }) => {
  const min = limits.min ?? -Infinity;
  const max = limits.max ?? Infinity;

  if (min > max) {
    return NaN;
  }

  return Math.max(min, Math.min(max, value));
};

export const divide = (numerator: number, denominator: number): number => {
  if (denominator === 0) return 0;

  return numerator / denominator;
};

export const roundToTwoDecimal = (value: number) => Math.round(value * 100) / 100;

export const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10;

export const roundToWholeNumber = (value: number) => Math.round(value);

export const isValidUsername = (username: string): boolean => {
  if (username.length > 20) {
    return false;
  }

  if (username.length < 3 || username.startsWith('_') || username.endsWith('_')) {
    return false;
  }

  const re = /^[a-z0-9_]+$/;
  return re.test(username);
};

/**
 * 01:25
 * 02:25:36
 */
export const secondsToClockFormat = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const remainderSeconds = seconds - hours * 3600 - minutes * 60;

  const paddWithLeadingZeros = (value: string): string => {
    if (value.length === 0) {
      return '00';
    }

    if (value.length === 1) {
      return `0${value}`;
    }

    return value;
  };

  if (hours) {
    const hoursString = paddWithLeadingZeros(`${hours}`);
    const minString = paddWithLeadingZeros(`${minutes}`);
    const secString = paddWithLeadingZeros(`${remainderSeconds}`);
    return `${hoursString}:${minString}:${secString}`;
  }

  return `${paddWithLeadingZeros(`${minutes}`)}:${paddWithLeadingZeros(`${remainderSeconds}`)}`;
};

/**
 * 10s
 * 1min 10s
 * 2h 4min 45s
 */
export const secondsToWordFormat = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const remainderSeconds = Math.floor(seconds % 60);

  if (hours) {
    return `${hours}h ${minutes}min ${remainderSeconds}s`;
  }

  if (minutes) {
    return `${minutes}min ${remainderSeconds}s`;
  }

  return `${seconds}s`;
};

/**
 * 14min
 * 2h 4min
 * 2h 0min
 */
export const secondsToWordFormatMinutes = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  if (hours) {
    return `${hours}h ${minutes}min`;
  }

  return `${minutes}min`;
};

export const isValidEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Matches strings with a base format of `domain.tld`.
 * - `domain` may contain letters, digits and dashes, but it can't begin or
 *   end with a dash
 * - `domain` may be prefixed once or multiple times with another `domain.`
 * - `tld` may be followed by `/path-to-page`
 * - the entire string may be prefixed by `https://` or `http://`
 */
const URL_REGEX = RegExp(
  [
    // optionally begin with http(s)://
    /(https?:\/\/)?/,

    // followed by the domain:
    // - any combination of letters, numbers and dashes
    // - but without a dash at the start or end
    // - maximum 255 characters per domain
    // - each domain ends with a `.`
    /((?!-)[-a-z0-9]{1,255}(?<!-)\.)+/,

    // followed by the top-level domain
    /[a-z]{2,10}/,

    // optionally followed by a path
    /(\/[-a-z0-9@:%_+.~#?!&/=]*)?/,
  ]
    .map(r => r.source)
    .join(''),
);

export const isValidWebUrl = (url: string): boolean => {
  return RegExp(`^${URL_REGEX.source}$`).test(String(url).toLowerCase());
};

/**
 * Check if a string is a valid phone number. A valid phone number is a string
 * that starts with a '+' followed by 9-15 digits with no spaces. This is the
 * format that we store in the database as well as what the Twilio API expects.
 *
 * @example
 * // valid:
 * +123456789
 * +123456789012345
 *
 * // not valid:
 * +1 23456789
 * 123456789
 * +1234567890123456
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  return typeof phoneNumber === 'string' && /^\+\d{9,15}$/.test(phoneNumber);
};

/**
 * Matches all UUID types, case-insensitive (matches both uppercase and
 * lowercase hexadecimal digits).
 */
export const isValidUuid = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return typeof uuid === 'string' && uuidRegex.test(uuid);
};

export const isNumber = (x: any): x is number => {
  return typeof x === 'number';
};

export const isWholeNumber = (value: number): boolean => {
  return value % 1 === 0;
};

/**
 * Return true is value is of format: NN:NN or NN:NN:NN
 */
export const isValidFormattedTime = (value: string): boolean => {
  if (value.length !== 5 && value.length !== 8) return false;

  if (value.length === 5) {
    return (
      /[0-9]/.test(value[0]) &&
      /[0-9]/.test(value[1]) &&
      value[2] === ':' &&
      /[0-9]/.test(value[3]) &&
      /[0-9]/.test(value[4])
    );
  }

  if (value.length === 8) {
    return (
      /[0-9]/.test(value[0]) &&
      /[0-9]/.test(value[1]) &&
      value[2] === ':' &&
      /[0-9]/.test(value[3]) &&
      /[0-9]/.test(value[4]) &&
      value[5] === ':' &&
      /[0-9]/.test(value[6]) &&
      /[0-9]/.test(value[7])
    );
  }

  return false;
};

export const formatDurationInput = (value: string): string | undefined => {
  if (isValidFormattedTime(value)) return value;

  if (value.length === 0) {
    return value;
  }

  if (!/^[0-9:]+$/i.test(value)) {
    return undefined;
  }

  if (value === '0') {
    return '';
  }

  if (value.length === 1) {
    return `00:0${value}`;
  }

  if (value.length === 4 && value[0] === '0' && value[1] === '0' && value[3] === '0') {
    // 00:0 -> ''
    return '';
  }

  if (value.length === 4) {
    // backspace
    // 12:3 -> 01:23
    return `0${value[0]}:${value[1]}${value[3]}`;
  }

  if (value.length === 6 && value[0] === '0') {
    // 01:234 -> 12:34
    return `${value[1]}${value[3]}:${value[4]}${value[5]}`;
  }

  // x:xx:xx (hour+ with no leading zero)
  if (/^[0-9]:[0-9]{2}:[0-9]{2}$/.test(value)) {
    return `0${value}`;
  }

  if (value.length === 7 && value[0] === '0') {
    // backspace
    // 01:23:4 -> 12:34
    return `${value[1]}${value[3]}:${value[4]}${value[6]}`;
  }

  if (value.length === 7) {
    // backspace
    // 12:34:5 -> 01:23:45
    return `0${value[0]}:${value[1]}${value[3]}:${value[4]}${value[6]}`;
  }

  if (value.length === 6 && value[0] !== '0') {
    // 12:345 -> 01:23:45
    return `0${value[0]}:${value[1]}${value[3]}:${value[4]}${value[5]}`;
  }

  if (value.length === 9) {
    // 01:23:456 -> 12:34:56
    return `${value[1]}${value[3]}:${value[4]}${value[6]}:${value[7]}${value[8]}`;
  }

  return undefined;
};

/**
 * Will always return a number. Ivalid strings return 0
 */
export const forceStringToNumber = (value?: string): number => {
  const numOrNaN = Number(value ? value.replace(',', '.') : 0);

  return numOrNaN ? numOrNaN : 0;
};

/**
 * Returns a number or undefined
 */
export const stringToNumber = (value?: string): number | undefined => {
  if (!value) return undefined;

  const numOrNaN = Number(value ? value.replace(',', '.') : 0);

  return isNaN(numOrNaN) ? undefined : numOrNaN;
};

/**
 * Returns the first non-undefined value produced by transform function being
 * applied to elements of the array in iteration order, or `undefined` if no
 * such value was produced. Equivalent to `firstNotNullOfOrNull` in Kotlin.
 */
export const findMapped = <T, R>(array: T[], transform: (element: T) => R): R | undefined => {
  for (const element of array) {
    const result = transform(element);

    if (result !== undefined) {
      return result;
    }
  }
};

/**
 * converts any array into an array of JSON chunks with a maximum given length
 * @param data an array of objects or primitives to be split into chunks
 * @param maxLength maximum length of any single returned JSON string
 * @returns an array of JSON strings, each one no longer than `maxLength`
 * @throws an error if fragmentation is impossible due to input data structure
 */
export const toFragmentedJSON = <T>(
  data: T[],
  maxLength: number,
  options: { lengthIn: 'utf8bytes' | 'characters' } = { lengthIn: 'utf8bytes' },
) => {
  const outputArray: string[] = [];

  const recurse = (left: number, right: number) => {
    if (left === right) return;

    // slice the array and convert it to JSON
    const sliceJson = JSON.stringify(data.slice(left, right));

    const length =
      options.lengthIn === 'characters' ? sliceJson.length : Buffer.byteLength(sliceJson, 'utf8');

    if (length <= maxLength) {
      // the size is good, push the chunk to the output array
      outputArray.push(sliceJson);
    } else if (right > left + 1) {
      // there are at least 2 elements in the current slice
      const middle = Math.ceil((left + right) / 2);
      recurse(left, middle);
      recurse(middle, right);
    } else {
      // we can't subdivide any further but the string is still too long
      throw new Error(`couldn't subdivide element`);
    }
  };

  outputArray.length = 0;
  recurse(0, data.length);

  return outputArray;
};

/**
 * Finds the closest data point that occurs before or at the target date using binary search.
 * Assumes input array is sorted by date in ascending order.
 *
 * @param data - Array of items containing dates in ascending order
 * @param dateExtractor - Function to extract Date from each item
 * @param targetDate - Target date to search for
 * @returns The closest item before or at the target date, or undefined if not found
 *
 * @example
 * const data = [
 *   { id: 1, date: dayjs('2023-01-01').toDate() },
 *   { id: 2, date: dayjs('2023-04-01').toDate() }
 * ];
 * const result = getClosestDataPointBeforeTargetDate(
 *   data,
 *   item => item.date,
 *   dayjs('2023-03-15').toDate(),
 * ); // Returns { id: 1, date: Date('2023-01-01') }
 */
export const getClosestDataPointBeforeTargetDate = <T>(
  data: T[],
  dateExtractor: (item: T) => Date,
  targetDate: Date,
): T | undefined => {
  if (!data.length) return undefined;

  let lowerIndex = 0;
  let upperIndex = data.length - 1;
  let middleIndex;

  do {
    middleIndex = Math.floor((lowerIndex + upperIndex) / 2);
    const middle = data[middleIndex];

    if (dateExtractor(middle) < targetDate) {
      lowerIndex = middleIndex + 1;
      continue;
    } else if (dateExtractor(middle) > targetDate) {
      upperIndex = middleIndex - 1;
      continue;
    } else {
      // found exact match
      return middle;
    }
  } while (lowerIndex <= upperIndex);

  if (dateExtractor(data[middleIndex]) < targetDate) {
    // the last closest match that was found is before the target
    return data[middleIndex];
  } else {
    // the last closest match that was found is just after the target, so take the previous one
    return data[middleIndex - 1] ?? undefined;
  }
};

/**
 * Finds the closest data point to the target date.
 *
 * @param data - Array of items containing dates
 * @param dateExtractor - Function to extract Date from each item
 * @param targetDate - Target date to search for
 * @returns The closest item to the target date, or undefined if array is empty
 *
 * @example
 * const data = [
 *   { id: 1, date: dayjs('2023-01-01').toDate() },
 *   { id: 2, date: dayjs('2023-04-01').toDate() }
 * ];
 * const result = getClosestDataPoint(
 *   data,
 *   item => item.date,
 *   dayjs('2023-03-15').toDate(),
 * ); // Returns { id: 2, date: Date('2023-04-01') }
 */
export const getClosestDataPointAroundTargetDate = <T>(
  data: T[],
  dateExtractor: (item: T) => Date,
  targetDate: Date,
): T | undefined => {
  if (!data.length) return undefined;

  return data.reduce(
    (closest, current) => {
      if (!closest) return current;

      const closestDiff = Math.abs(dateExtractor(closest).getTime() - targetDate.getTime());
      const currentDiff = Math.abs(dateExtractor(current).getTime() - targetDate.getTime());

      return currentDiff < closestDiff ? current : closest;
    },
    undefined as T | undefined,
  );
};

export const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Calculate the total duration of a workout in seconds
 */

export interface DurationCalculatetableWorkout {
  end_time: number;
  start_time: number;
}

export const workoutDurationSeconds = (workout: DurationCalculatetableWorkout): number => {
  const duration = workout.end_time - workout.start_time;
  return duration >= 0 ? duration : 0;
};

/**
 * Calculate the total reps in a workout
 */

export interface TotalRepsCalculatetableWorkout {
  exercises: Array<{
    sets: Array<{
      reps?: number | null;
    }>;
  }>;
}

export const workoutReps = (workout: TotalRepsCalculatetableWorkout): number => {
  return workout.exercises.reduce((exerciseAccu, exercise) => {
    const exerciseReps = exercise.sets.reduce((setAccu, set) => {
      if (set.reps) {
        return setAccu + set.reps;
      }

      return setAccu;
    }, 0);

    return exerciseAccu + exerciseReps;
  }, 0);
};

/**
 * Calculate the total distance in a workout in meters
 */

export interface TotalDistanceCalculatetableWorkout {
  exercises: Array<{
    sets: Array<{
      distance_meters?: number | null;
    }>;
  }>;
}

export const workoutDistanceMeters = (workout: TotalDistanceCalculatetableWorkout): number => {
  return workout.exercises.reduce((exerciseAccu, exercise) => {
    const exerciseDistance = exercise.sets.reduce((setAccu, set) => {
      if (set.distance_meters) {
        return setAccu + set.distance_meters;
      }

      return setAccu;
    }, 0);

    return exerciseAccu + exerciseDistance;
  }, 0);
};

/**
 * Calculate the total set count in a workout
 */

export interface TotalSetCountWorkout {
  exercises: Array<{ sets: Array<any> }>;
}

/**
 * Calculate the set weight for a given user exercise set
 * to be used in the exercise stats calculations on the web and coach app
 */
export const userExerciseSetWeight = (
  set: UserExerciseSet,
  exerciseStore: BaseExerciseTemplate[],
  hundredPercentBodyweightExercise: boolean,
): number => {
  if (!set) return 0;

  const exercise = exerciseStore.find(e => e.id === set.exercise_template_id);

  if (!exercise) {
    return num(set.weight_kg);
  }

  if (exercise.exercise_type === 'bodyweight_reps') {
    return hundredPercentBodyweightExercise
      ? num(set.user_bodyweight_kg) + num(set.weight_kg)
      : num(set.weight_kg);
  } else if (exercise.exercise_type === 'bodyweight_assisted_reps') {
    return hundredPercentBodyweightExercise
      ? Math.max(num(set.user_bodyweight_kg) - num(set.weight_kg), 0)
      : 0;
  } else if (exercise.exercise_type === 'reps_only') {
    return hundredPercentBodyweightExercise ? num(set.user_bodyweight_kg) : 0;
  } else {
    return num(set.weight_kg);
  }
};

export const workoutSetCount = (w: TotalSetCountWorkout): number => {
  return w.exercises.reduce((accu, exercise) => {
    return accu + exercise.sets.length;
  }, 0);
};

export const UserFacingIndicatorToSetIndicator = (indicator: UserFacingSetIndicator): SetType => {
  switch (indicator) {
    case 'dropset':
      return 'dropset';
    case 'warmup':
      return 'warmup';
    case 'failure':
      return 'failure';
    default:
      return 'normal';
  }
};

interface GetEstimatedExercisesDuration {
  exercises: {
    rest_seconds: number | null;
    exercise_type: ExerciseType;
    sets: {
      duration_seconds?: number | null;
      indicator: SetType;
    }[];
  }[];
}

export const ESTIMATED_SET_DURATION = 45;
export const ESTIMATED_REST_TIMER_DURATION = 90;

const isDurationExercise = (type: ExerciseType): boolean => {
  return (
    type === 'duration' ||
    type === 'weight_duration' ||
    type === 'distance_duration' ||
    type === 'floors_duration' ||
    type === 'steps_duration'
  );
};

export const getEstimatedExercisesDurationSeconds = ({
  exercises,
}: GetEstimatedExercisesDuration) => {
  const totalSeconds = exercises.reduce((exercisesTotal, exercise) => {
    const restPerSet = exercise.rest_seconds ?? ESTIMATED_REST_TIMER_DURATION;

    const setsTotal = exercise.sets.reduce((setTotal, set) => {
      // Sometimes we get 0 values for duration on exercises that aren't duration.
      // Added this check to prevent having a estimated duration of 0 for these.
      const duration = isDurationExercise(exercise.exercise_type)
        ? (set.duration_seconds ?? ESTIMATED_SET_DURATION)
        : ESTIMATED_SET_DURATION;

      // if it is a dropset, we don't add the rest time
      if (set.indicator === 'dropset') return setTotal + duration;

      return setTotal + duration + restPerSet;
    }, 0);

    return exercisesTotal + setsTotal;
  }, 0);

  return totalSeconds;
};

export const oneRepMaxPercentageMap: { [s: number]: number } = {
  1: 1.0,
  2: 0.97,
  3: 0.94,
  4: 0.92,
  5: 0.89,
  6: 0.86,
  7: 0.83,
  8: 0.81,
  9: 0.78,
  10: 0.75,
  11: 0.73,
  12: 0.71,
  13: 0.7,
  14: 0.68,
  15: 0.67,
  16: 0.65,
  17: 0.64,
  18: 0.63,
  19: 0.61,
  20: 0.6,
  21: 0.59,
  22: 0.58,
  23: 0.57,
  24: 0.56,
  25: 0.55,
  26: 0.54,
  27: 0.53,
  28: 0.52,
  29: 0.51,
  30: 0.5,
};

export const oneRepMax = (weight: number, reps: number): number => {
  if (reps === 0) {
    return 0;
  }

  const percent = reps > 30 ? 0.5 : (oneRepMaxPercentageMap[reps] as number);

  return weight / percent;
};

export const setVolume = (weight: number, reps: number): number => {
  return weight * reps;
};

const exerciseTypeSupportsPRType = (
  exerciseType: ExerciseType,
  supportedPRTypes: { [exerciseType in ExerciseType]: boolean },
) => supportedPRTypes[exerciseType];

export const supportsWeightPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    weight_reps: true,
    bodyweight_reps: true,
    short_distance_weight: true,
    weight_duration: true,
    bodyweight_assisted_reps: false,
    reps_only: false,
    distance_duration: false,
    duration: false,
    floors_duration: false,
    steps_duration: false,
  });

export const supportsVolumePR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    weight_reps: true,
    short_distance_weight: true,
    weight_duration: true,
    bodyweight_reps: true,
    bodyweight_assisted_reps: false,
    reps_only: false,
    distance_duration: false,
    duration: false,
    floors_duration: false,
    steps_duration: false,
  });

export const supportsOneRepMaxPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    weight_reps: true,
    bodyweight_reps: false,
    bodyweight_assisted_reps: false,
    reps_only: false,
    distance_duration: false,
    duration: false,
    short_distance_weight: false,
    weight_duration: false,
    floors_duration: false,
    steps_duration: false,
  });

export const supportsRepsPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    bodyweight_assisted_reps: true,
    reps_only: true,
    weight_reps: false,
    bodyweight_reps: false,
    distance_duration: false,
    duration: false,
    short_distance_weight: false,
    weight_duration: false,
    floors_duration: false,
    steps_duration: false,
  });

export const supportsDistancePR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    distance_duration: true,
    short_distance_weight: true,
    weight_reps: false,
    bodyweight_reps: false,
    bodyweight_assisted_reps: false,
    reps_only: false,
    duration: false,
    weight_duration: false,
    floors_duration: false,
    steps_duration: false,
  });

export const supportsDurationPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    distance_duration: true,
    duration: true,
    weight_duration: true,
    weight_reps: false,
    bodyweight_reps: false,
    bodyweight_assisted_reps: false,
    reps_only: false,
    short_distance_weight: false,
    floors_duration: true,
    steps_duration: true,
  });

export const supportsFloorsPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    distance_duration: false,
    short_distance_weight: false,
    weight_reps: false,
    bodyweight_reps: false,
    bodyweight_assisted_reps: false,
    reps_only: false,
    duration: false,
    weight_duration: false,
    floors_duration: true,
    steps_duration: false,
  });

export const supportsStepsPR = (exerciseType: ExerciseType) =>
  exerciseTypeSupportsPRType(exerciseType, {
    distance_duration: false,
    short_distance_weight: false,
    weight_reps: false,
    bodyweight_reps: false,
    bodyweight_assisted_reps: false,
    reps_only: false,
    duration: false,
    weight_duration: false,
    floors_duration: false,
    steps_duration: true,
  });

/** @deprecated use `numberToLocaleString` */
export const numberWithCommas = (x: number): string => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Formats a number into a string, accounting for the system locale.
 *
 * @example
 * numberToLocaleString(1234.567) === '1,234.567' // English (UK / US)
 * numberToLocaleString(1234.567) === '1.234,567' // German (Germany)
 * numberToLocaleString(1234.567) === '1 234,567' // French (France)
 */
export const numberToLocaleString = (
  value: number,
  options?: { maximumFractionDigits?: number },
): string => value.toLocaleString(undefined, { ...options });

export const getStrengthLevelFromPercentile = (percentile: number): StrengthLevel => {
  if (percentile >= 90) {
    return 'elite';
  } else if (percentile >= 70) {
    return 'advanced';
  } else if (percentile >= 40) {
    return 'intermediate';
  } else {
    return 'beginner';
  }
};

export const isBaseExerciseTemplate = (x: any): x is BaseExerciseTemplate => {
  return (
    x.id !== undefined &&
    x.title !== undefined &&
    x.muscle_group !== undefined &&
    x.exercise_type !== undefined &&
    x.equipment_category !== undefined
  );
};

const _v4uuidCheckRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-([0-9a-f])[0-9a-f]{3}-([0-9a-f])[0-9a-f]{3}-([0-9a-f]{12})/i;

type GenerateUserGroupError =
  | 'invalid-number-of-groups'
  | 'invalid-uuid'
  | 'uuid-not-v4'
  | 'invalid-variant';

/**
 * Generates a subsample or test group given a user id. Technically, it just
 * calculates a trivial checksum of the last (random) part of a v4 UUID.
 * @param userId a v4 UUID; _must_ be v4 to ensure random distribution
 * @param numGroups number of possible groups, from 2 to 2^32
 * @returns a number in the `[0, numGroups)` range, always equal for a given
 * `(userId, numGroups)` pair, or an error, inside a Result<T> object
 */
export const generateUserGroup = (
  userId: string,
  numGroups: number,
): Result<number, GenerateUserGroupError> => {
  if (numGroups < 2 || numGroups > 2 ** 32) {
    return { isSuccess: false, error: 'invalid-number-of-groups' };
  }

  const match = _v4uuidCheckRegex.exec(userId);

  if (!match) {
    return { isSuccess: false, error: 'invalid-uuid' };
  }

  const [, version, variant, tail] = match;

  if (version !== '4') {
    return { isSuccess: false, error: 'uuid-not-v4' };
  }

  if (!['8', '9', 'a', 'b', 'c', 'd'].includes(variant.toLowerCase())) {
    return { isSuccess: false, error: 'invalid-variant' };
  }

  let value: number;

  try {
    const idHexString = `0x${tail}`;
    const idBigInt = BigInt(idHexString);
    const numGroupsBigInt = BigInt(numGroups);
    const remainderBigInt = idBigInt % numGroupsBigInt;
    value = Number(remainderBigInt);
  } catch (e) {
    throw new Error(`generateUserGroup: tail="${tail}": ${(e as Error)?.message}`);
  }

  return { isSuccess: true, value };
};

/**
 * Get the user group value for a given user id and number of groups.
 * @param userId a v4 UUID; _must_ be v4 to ensure random distribution
 * @param numGroups number of possible groups, from 2 to 2^32
 * @returns User group value (A, B, C, etc.), or undefined if the user id is not a v4 UUID
 * or if an error occurs.
 */
export const generateUserGroupValue = (
  userId: string,
  numGroups: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10,
) => {
  const group = generateUserGroup(userId, numGroups);

  if (group.isSuccess) {
    switch (group.value) {
      case 0:
        return 'A';
      case 1:
        return 'B';
      case 2:
        return 'C';
      case 3:
        return 'D';
      case 4:
        return 'E';
      case 5:
        return 'F';
      case 6:
        return 'G';
      case 7:
        return 'H';
      case 8:
        return 'I';
      case 9:
        return 'J';
      default:
        return undefined;
    }
  } else {
    return undefined;
  }
};

/**
 * @example
 * isVersionAGreaterOrEqualToVersionB('1.2.3', '1.2')    // true
 * isVersionAGreaterOrEqualToVersionB('1.2.3', '1.2.2')  // true
 * isVersionAGreaterOrEqualToVersionB('1.2.3', '1.2.3')  // true
 *
 * isVersionAGreaterOrEqualToVersionB('1.2.3', '1.2.4')  // false
 * isVersionAGreaterOrEqualToVersionB('1.2.3', '1.3')    // false
 */
export const isVersionAGreaterOrEqualToVersionB = (versionA: string, versionB: string) => {
  const versionAparts = `${versionA}`.split('.');
  const versionBparts = `${versionB}`.split('.');

  const longestVersionLength = Math.max(versionAparts.length, versionBparts.length);

  for (let i = 0; i < longestVersionLength; i++) {
    const versionApart = versionAparts[i] ?? 0;
    const versionBpart = versionBparts[i] ?? 0;

    if (parseInt(versionApart, 10) > parseInt(versionBpart, 10)) {
      return true;
    }

    if (parseInt(versionApart, 10) < parseInt(versionBpart, 10)) {
      return false;
    }

    // if equal, continue to next part
  }

  // the versions are entirely equal
  return true;
};

export const splitAtUsernamesAndLinks = (text: string): FormatAtText[] => {
  const mentionsRegex = /\B@[a-z0-9_-]+/gi;
  const linksRegex = URL_REGEX;

  const regex = new RegExp(`(${mentionsRegex.source}|${linksRegex.source})`, 'gi');

  const matches = text.match(regex);

  const format: FormatAtText[] = [];

  if (!matches) {
    return [{ format: 'none', text }];
  }

  let lastIndex = 0;
  matches.forEach(match => {
    const index = text.indexOf(match, lastIndex);
    if (index > lastIndex) {
      format.push({ format: 'none', text: text.substring(lastIndex, index) });
    }
    if (match.match(linksRegex)) {
      format.push({ format: 'link', text: match });
    } else if (match.match(mentionsRegex)) {
      format.push({ format: '@', text: match });
    }

    lastIndex = index + match.length;
  });

  if (lastIndex < text.length) {
    format.push({ format: 'none', text: text.substring(lastIndex) });
  }

  return format;
};

// Single comprehensive YouTube URL regex for both validation and ID extraction
const YOUTUBE_URL_REGEX =
  /^(?:https?:\/\/)?(?:(?:www\.)?youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/|user\/[^/]+#p\/|attribution_link\?a=|channel\/|c\/|playlist\?list=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[/?#&].*)?$/;

export const validateYoutubeUrl = (url: string): boolean => {
  return YOUTUBE_URL_REGEX.test(url);
};

export const getYoutubeVideoId = (url: string): string | undefined => {
  const match = url.match(YOUTUBE_URL_REGEX);
  return match && match[1] && match[1].length === 11 ? match[1] : undefined;
};

/**@param workouts must be sorted descending by start_time */
export const calculateCurrentWeekStreak = (
  workouts: { start_time: number }[],
  firstWeekday: Weekday,
  untilUnix: number = dayjs().unix(),
): number => {
  let streakCount = 0;

  let weekStart = startOfWeek(dayjs.unix(untilUnix), firstWeekday).subtract(1, 'week');
  let weekEnd = weekStart.add(1, 'week');

  let i = 0;

  /** Iterate through the workouts from most recent to least and keep track of the week-long
   * sliding window in which there needs to be a workout present in order for the streak to count.
   * If there is a workout present during this window, move the window into the past by a week
   * and keep iterating through the workouts. If there are no workouts during this window,
   * the streak count is not incremented.
   */
  while (i < workouts.length) {
    const workoutStart = workouts[i].start_time;

    if (workoutStart >= weekStart.unix() && workoutStart <= weekEnd.unix()) {
      streakCount += 1;
      weekStart = weekStart.subtract(1, 'week');
      weekEnd = weekEnd.subtract(1, 'week');
    } else if (workoutStart < weekStart.unix()) {
      /** This workout is before the sliding window. We don't get to this point unless
       * the user didn't workout during the window. So that means the streak is over
       */
      break;
    }

    i += 1;
  }

  // check if the current week has any workouts
  weekStart = startOfWeek(dayjs.unix(untilUnix), firstWeekday);
  weekEnd = weekStart.add(1, 'week');

  if (
    workouts.find(
      ({ start_time }) => start_time >= weekStart.unix() && start_time <= weekEnd.unix(),
    )
  ) {
    streakCount += 1;
  }

  return streakCount;
};

const startOfWeek = (d: Dayjs, firstDayOfWeek: Weekday): Dayjs => {
  const firstDayOfWeekIndex = weekdayNumberMap[firstDayOfWeek];
  let testDay = d;
  while (testDay.day() !== firstDayOfWeekIndex) {
    testDay = testDay.subtract(1, 'day');
  }

  return testDay.startOf('day');
};

export const weekdayNumberMap: { [key in Weekday]: number } = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const distance = (value: number, distanceUnit: DistanceUnit) => {
  if (distanceUnit === 'miles') {
    const unrounded = exactMetersToMiles(value);
    return roundToTwoDecimal(unrounded);
  }

  return roundToTwoDecimal(value / 1000);
};

export const weight = (value: number, weightUnit: WeightUnit) => {
  if (weightUnit === 'lbs') {
    return roundToTwoDecimal(exactKgtoLbs(value));
  }

  return roundToTwoDecimal(value);
};

interface getSetValueParams {
  exerciseType: ExerciseType;
  set: {
    weight_kg: number | null;
    reps: number | null;
    duration_seconds: number | null;
    distance_meters: number | null;
    custom_metric: number | null;
  };
  units: {
    weight: WeightUnit;
    distance: DistanceUnit;
  };
  lokalizedLabels: {
    kg: string;
    lbs: string;
    km: string;
    mi: string;
    m: string;
    yd: string;
    steps: string;
    floors: string;
  };
}

type ShortDistanceUnitShort = 'm' | 'yd';

export const formatSetValue = ({
  exerciseType,
  set,
  units,
  lokalizedLabels,
}: getSetValueParams) => {
  const unitToLabelMap: Record<
    WeightUnit | DistanceUnitShort | ShortDistanceUnitShort | 'steps' | 'floors',
    string
  > = {
    kg: lokalizedLabels.kg,
    lbs: lokalizedLabels.lbs,
    km: lokalizedLabels.km,
    mi: lokalizedLabels.mi,
    m: lokalizedLabels.m,
    yd: lokalizedLabels.yd,
    steps: lokalizedLabels.steps,
    floors: lokalizedLabels.floors,
  };

  switch (exerciseType) {
    case 'weight_reps':
    case 'bodyweight_reps': {
      const weightLabel = `${weight(set.weight_kg || 0, units.weight)} ${
        unitToLabelMap[units.weight]
      }`;
      const reps = `${set.reps ?? 0}`;
      return `${weightLabel} x ${reps}`;
    }

    case 'bodyweight_assisted_reps': {
      const weightLabel = `-${weight(set.weight_kg || 0, units.weight)} ${
        unitToLabelMap[units.weight]
      }`;
      const reps = `${set.reps ?? 0}`;
      return `${weightLabel} x ${reps}`;
    }

    case 'reps_only':
      return `${set.reps ?? 0}`;

    case 'distance_duration': {
      const distanceUnitShort = units.distance === 'kilometers' ? 'km' : 'mi';

      const distanceValue = `${distance(set.distance_meters || 0, units.distance)} ${
        unitToLabelMap[distanceUnitShort]
      }`;
      const duration = `${secondsToWordFormat(Number(set.duration_seconds))}`;
      return `${distanceValue} - ${duration}`;
    }

    case 'duration':
      return secondsToWordFormat(Number(set.duration_seconds));

    case 'short_distance_weight': {
      const weightLabel = `${weight(set.weight_kg || 0, units.weight)} ${
        unitToLabelMap[units.weight]
      }`;
      const shortDistance = roundToTwoDecimal(set.distance_meters || 0);

      const shortDistanceUnitShort = units.distance === 'kilometers' ? 'm' : 'yd';
      const shortDistanceUnitShortLabel = unitToLabelMap[shortDistanceUnitShort];
      return `${weightLabel} - ${shortDistance} ${shortDistanceUnitShortLabel}`;
    }

    case 'weight_duration': {
      const weightLabel = `${weight(set.weight_kg || 0, units.weight)} ${
        unitToLabelMap[units.weight]
      }`;
      const duration = `${secondsToWordFormat(Number(set.duration_seconds))}`;
      return `${weightLabel} - ${duration}`;
    }

    case 'floors_duration':
      return `${set.custom_metric ? roundToWholeNumber(set.custom_metric) : 0} ${
        unitToLabelMap.floors
      } - ${secondsToWordFormat(Number(set.duration_seconds))}`;

    case 'steps_duration':
      return `${set.custom_metric ? roundToWholeNumber(set.custom_metric) : 0} ${
        unitToLabelMap.steps
      } - ${secondsToWordFormat(Number(set.duration_seconds))}`;
    default:
      exhaustiveTypeCheck(exerciseType);
      return '';
  }
};

export const rawInstructionsToIndexedSteps = (rawInstructions: string) => {
  const instructionsByLine: string[] = rawInstructions
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  const instructionsByStep: ExerciseInstructionsStep[] = instructionsByLine.map(s => {
    const parsedStep = s.match(/^([0-9]*)\\?\.(.*)$/);
    if (!parsedStep) {
      // this line doesn't contain a number, so just return it unchanged
      return { description: s };
    }

    const index = Number(parsedStep[1]) || undefined; // if it's NaN or 0, convert it to undefined
    const description = parsedStep[2]?.trim() ?? s;

    return { index, description };
  });

  return instructionsByStep;
};

const roundToKnownValue = (value: number, knownValues: number[]): number | undefined => {
  if (knownValues.length === 0) return undefined;

  const boundingValues = knownValues.reduce(
    ([lower, upper], v) => {
      return [v > lower && v <= value ? v : lower, v < upper && v >= value ? v : upper];
    },
    [-Infinity, Infinity],
  );

  if (boundingValues[0] === -Infinity && boundingValues[1] === Infinity) return undefined;
  if (boundingValues[0] === -Infinity) return boundingValues[1];
  if (boundingValues[1] === Infinity) return boundingValues[0];

  return value - boundingValues[0] < boundingValues[1] - value
    ? boundingValues[0]
    : boundingValues[1];
};

export const indexByNearestValue = <T>(value: number, map: { [key: number]: T }): T | undefined => {
  const index = roundToKnownValue(
    value,
    Object.keys(map).map(e => Number(e)),
  );
  return index ? map[index] : undefined;
};

// Redirect targets that come from a URL query parameter are whatever the person who wrote the
// link typed, so `javascript:alert(1)` would run in the logged-in user's tab. Only keep paths
// that stay on our own site
export const safeRedirectPath = (path: unknown, fallback: string): string => {
  if (typeof path !== 'string') return fallback;
  const cleaned = path.replace(/[\t\n\r]/g, '');
  if (!cleaned.startsWith('/')) return fallback;
  if (cleaned.startsWith('//') || cleaned.startsWith('/\\')) return fallback;
  return cleaned;
};
